import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  User, LogOut, MapPin, CreditCard, Plus, Trash2, Loader2, Home, Package,
  ArrowLeft, CheckCircle2, Clock, Truck, Banknote,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Navbar";

const STATUS = {
  new: ["New", "bg-slate-100 text-slate-600"],
  assigned: ["Assigned", "bg-blue-50 text-blue-700"],
  in_transit: ["In Transit", "bg-amber-50 text-amber-700"],
  delivered: ["Delivered", "bg-green-50 text-green-700"],
};

export default function Account() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("profile");

  const [profile, setProfile] = useState({ name: "", phone: "", preferred_payment: "card" });
  const [addresses, setAddresses] = useState([]);
  const [newAddr, setNewAddr] = useState({ label: "", address: "", suburb: "" });
  const [cards, setCards] = useState([]);
  const [orders, setOrders] = useState([]);
  const [credit, setCredit] = useState({ balance: 0, ledger: [] });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingAddr, setSavingAddr] = useState(false);
  const [addingCard, setAddingCard] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const { data } = await api.get("/account/profile");
      setProfile({ name: data.name || "", phone: data.phone || "", preferred_payment: data.preferred_payment || "card" });
      setAddresses(data.addresses || []);
    } catch { /* ignore */ }
    api.get("/account/orders").then(({ data }) => setOrders(data || [])).catch(() => {});
    api.get("/account/cards").then(({ data }) => setCards(data.cards || [])).catch(() => {});
    api.get("/account/credit").then(({ data }) => setCredit(data || { balance: 0, ledger: [] })).catch(() => {});
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (params.get("card") === "added") {
      toast.success("Card saved");
      setTab("payment");
      api.get("/account/cards").then(({ data }) => setCards(data.cards || [])).catch(() => {});
      refresh();
      params.delete("card");
      setParams(params, { replace: true });
    }
  }, [params, setParams, refresh]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put("/account/profile", profile);
      await refresh();
      toast.success("Profile saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingProfile(false); }
  };

  const persistAddresses = async (next) => {
    setSavingAddr(true);
    try {
      const { data } = await api.put("/account/profile", { addresses: next });
      setAddresses(data.addresses || []);
      await refresh();
      return true;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
    finally { setSavingAddr(false); }
  };

  const addAddress = async () => {
    if (!newAddr.address || !newAddr.suburb) { toast.error("Enter an address and suburb"); return; }
    const ok = await persistAddresses([...addresses, newAddr]);
    if (ok) { setNewAddr({ label: "", address: "", suburb: "" }); toast.success("Address saved"); }
  };

  const removeAddress = async (id) => {
    const ok = await persistAddresses(addresses.filter((a) => a.id !== id));
    if (ok) toast.success("Address removed");
  };

  const addCard = async () => {
    setAddingCard(true);
    try {
      const { data } = await api.post("/account/cards/setup", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setAddingCard(false); }
  };

  const removeCard = async (pm) => {
    try { await api.delete(`/account/cards/${pm}`); setCards((c) => c.filter((x) => x.id !== pm)); await refresh(); toast.success("Card removed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const upcoming = orders.filter((o) => o.order_status !== "delivered");
  const pastOrders = orders.filter((o) => o.order_status === "delivered");

  const TABS = [
    ["profile", "Profile", User],
    ["addresses", "Addresses", MapPin],
    ["payment", "Payment", CreditCard],
    ["orders", "Orders", Package],
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-1.5">
            <Link to="/book" className="text-sm font-semibold text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg" data-testid="account-book-link">Book delivery</Link>
            <Link to="/" className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" title="Home"><Home className="w-5 h-5" /></Link>
            <button onClick={doLogout} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 flex items-center gap-1.5 text-sm font-medium" data-testid="account-logout-btn">
              <LogOut className="w-5 h-5" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full brand-gradient flex items-center justify-center text-white font-bold text-lg">{(user?.name || "?").charAt(0).toUpperCase()}</div>
          <div>
            <h1 className="font-heading text-2xl font-extrabold text-slate-900 leading-tight">Hi, {user?.name?.split(" ")[0] || "there"}</h1>
            <p className="text-slate-500 text-sm">{user?.email}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-6 flex items-center justify-between" data-testid="account-credit-card">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Store credit</div>
            <div className="font-display text-3xl font-extrabold text-slate-900 mt-1" data-testid="account-credit-balance">${credit.balance.toFixed(2)}</div>
            <div className="text-xs text-slate-500 mt-1">Earn credit on every order — use it any time at checkout.</div>
          </div>
          <Link to="/book" className="brand-gradient text-white font-semibold px-5 py-2.5 rounded-full text-sm shrink-0">Use it</Link>
        </div>

        <div className="inline-flex gap-1 mb-6 bg-white border border-slate-200 rounded-full p-1 shadow-sm flex-wrap">
          {TABS.map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`account-tab-${id}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1.5 transition-colors ${tab === id ? "brand-gradient text-white shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {tab === "profile" && (
          <Card title="Your details" testid="account-profile-panel">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name" value={profile.name} onChange={(v) => setProfile({ ...profile, name: v })} testid="profile-name" />
              <Field label="Phone" value={profile.phone} onChange={(v) => setProfile({ ...profile, phone: v })} testid="profile-phone" />
            </div>
            <div className="mt-4">
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Preferred payment</label>
              <div className="grid grid-cols-2 gap-3 max-w-sm">
                <button onClick={() => setProfile({ ...profile, preferred_payment: "card" })} data-testid="profile-pay-card"
                  className={`rounded-xl border p-3 flex items-center gap-2 text-sm font-semibold ${profile.preferred_payment === "card" ? "border-blue-600 bg-blue-50 ring-2 ring-blue-100" : "border-slate-200"}`}><CreditCard className="w-5 h-5 text-blue-600" /> Card</button>
                <button onClick={() => setProfile({ ...profile, preferred_payment: "cod" })} data-testid="profile-pay-cod"
                  className={`rounded-xl border p-3 flex items-center gap-2 text-sm font-semibold ${profile.preferred_payment === "cod" ? "border-blue-600 bg-blue-50 ring-2 ring-blue-100" : "border-slate-200"}`}><Banknote className="w-5 h-5 text-green-600" /> On delivery</button>
              </div>
            </div>
            <button onClick={saveProfile} disabled={savingProfile} className="brand-gradient btn-shine text-white font-semibold px-6 py-2.5 rounded-full mt-5 flex items-center gap-2 disabled:opacity-60" data-testid="profile-save-btn">
              {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
            </button>
          </Card>
        )}

        {tab === "addresses" && (
          <Card title="Saved addresses" testid="account-addresses-panel">
            <div className="space-y-2.5 mb-5">
              {addresses.length === 0 && <p className="text-sm text-slate-400">No saved addresses yet.</p>}
              {addresses.map((a) => (
                <div key={a.id} className="flex items-start justify-between border border-slate-200 rounded-xl px-4 py-3" data-testid={`address-${a.id}`}>
                  <div className="flex items-start gap-2.5">
                    <MapPin className="w-4 h-4 text-blue-600 mt-0.5" />
                    <div>
                      {a.label && <div className="text-xs font-bold text-blue-700 uppercase tracking-wide">{a.label}</div>}
                      <div className="text-sm text-slate-800">{a.address}</div>
                      <div className="text-xs text-slate-500">{a.suburb}</div>
                    </div>
                  </div>
                  <button onClick={() => removeAddress(a.id)} className="text-slate-300 hover:text-red-500 p-1" data-testid={`address-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-100 pt-4">
              <div className="text-sm font-semibold text-slate-800 mb-3">Add an address</div>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Label (e.g. Home)" value={newAddr.label} onChange={(v) => setNewAddr({ ...newAddr, label: v })} testid="new-address-label" />
                <Field label="Street address" value={newAddr.address} onChange={(v) => setNewAddr({ ...newAddr, address: v })} testid="new-address-street" />
                <Field label="Suburb" value={newAddr.suburb} onChange={(v) => setNewAddr({ ...newAddr, suburb: v })} testid="new-address-suburb" />
              </div>
              <button onClick={addAddress} disabled={savingAddr} className="brand-gradient text-white font-semibold px-5 py-2.5 rounded-full mt-4 flex items-center gap-2 disabled:opacity-60" data-testid="add-address-btn">
                {savingAddr ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Add address</>}
              </button>
            </div>
          </Card>
        )}

        {tab === "payment" && (
          <Card title="Saved cards" testid="account-payment-panel">
            <p className="text-sm text-slate-500 mb-4">Save a card to pay in one tap next time. Your card details are stored securely by Stripe — never on our servers.</p>
            <div className="space-y-2.5 mb-5">
              {cards.length === 0 && <p className="text-sm text-slate-400">No saved cards yet.</p>}
              {cards.map((c) => (
                <div key={c.id} className="flex items-center justify-between border border-slate-200 rounded-xl px-4 py-3" data-testid={`card-${c.id}`}>
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                    <div>
                      <div className="text-sm font-semibold text-slate-800 capitalize">{c.brand} •••• {c.last4}</div>
                      <div className="text-xs text-slate-500">Expires {String(c.exp_month).padStart(2, "0")}/{c.exp_year}</div>
                    </div>
                  </div>
                  <button onClick={() => removeCard(c.id)} className="text-slate-300 hover:text-red-500 p-1" data-testid={`card-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <button onClick={addCard} disabled={addingCard} className="brand-gradient btn-shine text-white font-semibold px-6 py-2.5 rounded-full flex items-center gap-2 disabled:opacity-60" data-testid="add-card-btn">
              {addingCard ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Add a card</>}
            </button>
          </Card>
        )}

        {tab === "orders" && (
          <Card title="Your deliveries" testid="account-orders-panel">
            {orders.length === 0 && <p className="text-sm text-slate-400">No orders yet. <Link to="/book" className="text-blue-600 font-semibold">Book your first delivery</Link>.</p>}
            {upcoming.length > 0 && <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5"><Clock className="w-4 h-4" /> Upcoming</div>}
            <div className="space-y-2.5 mb-6">
              {upcoming.map((o) => <OrderRow key={o.id} o={o} />)}
            </div>
            {pastOrders.length > 0 && <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Past</div>}
            <div className="space-y-2.5">
              {pastOrders.map((o) => <OrderRow key={o.id} o={o} />)}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

const OrderRow = ({ o }) => {
  const [label, cls] = STATUS[o.order_status] || STATUS.new;
  return (
    <div className="border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3" data-testid={`order-${o.id}`}>
      <div className="min-w-0">
        <div className="font-semibold text-slate-800 text-sm flex items-center gap-2"><Truck className="w-4 h-4 text-blue-500" /> {o.scheduled_date} · {o.suburb}</div>
        <div className="text-xs text-slate-500 mt-0.5">{o.water_type === "pure" ? "Pure" : "Town"} · {o.loads || 1} load{(o.loads || 1) > 1 ? "s" : ""} · ${o.amount} · {o.payment_status === "paid" ? "Paid" : o.payment_method === "cod" ? "Pay on delivery" : "Payment pending"}</div>
      </div>
      <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`}>{label}</span>
    </div>
  );
};

const Card = ({ title, testid, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid={testid}>
    <h2 className="font-heading font-bold text-lg text-slate-900 mb-4">{title}</h2>
    {children}
  </div>
);

const Field = ({ label, value, onChange, testid, type = "text" }) => (
  <div>
    <label className="text-xs font-medium text-slate-600 mb-1.5 block">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-white text-sm" />
  </div>
);
